import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Calendar as CalendarIcon,
  CalendarCheck,
  CalendarClock,
  CalendarX,
  Bell,
  ClipboardList,
  Stethoscope,
  ChevronRight,
  Mail,
  Phone,
  Smile,
  Building2,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import moment from 'moment';
import { apiService, Appointment, GoogleCalendarEvent, Provider, Procedure, Operatory } from '../../services/apiService';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import StatusPill, { StatusTone } from './ui/StatusPill';

interface DashboardStats {
  todaysAppointments: number;
  todaysGoogleOnly: number; // subset of todaysAppointments that are Google-only events
  completedAppointments: number;
  completedGoogleOnly: number;
  pendingConfirmations: number;
  noShows: number;
}

type UpcomingItem =
  | { kind: 'appt'; sortKey: string; appt: Appointment }
  | { kind: 'google'; sortKey: string; gevent: GoogleCalendarEvent; date: string; time: string };

const getGreeting = () => {
  const h = new Date().getHours();
  if (h < 11) return 'Guten Morgen';
  if (h < 18) return 'Guten Tag';
  return 'Guten Abend';
};

const STATUS_META: Record<string, { label: string; tone: StatusTone }> = {
  confirmed: { label: 'Bestätigt', tone: 'success' },
  pending: { label: 'Offen', tone: 'warning' },
  scheduled: { label: 'Geplant', tone: 'info' },
  completed: { label: 'Abgeschlossen', tone: 'info' },
  cancelled: { label: 'Storniert', tone: 'danger' },
  no_show: { label: 'Nicht erschienen', tone: 'neutral' },
};

interface StatCardProps {
  icon: LucideIcon;
  label: string;
  value: number;
  tone: 'primary' | 'success' | 'warning' | 'danger';
  subtitle?: string;
}

const TONE_CHIP: Record<StatCardProps['tone'], string> = {
  primary: 'bg-brand-accent/15 text-brand-primary',
  success: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-400',
  warning: 'bg-amber-50 text-amber-600 dark:bg-amber-500/10 dark:text-amber-400',
  danger: 'bg-rose-50 text-rose-600 dark:bg-rose-500/10 dark:text-rose-400',
};

const StatCard: React.FC<StatCardProps> = ({ icon: Icon, label, value, tone, subtitle }) => (
  <div className="rounded-xl bg-brand-surface border border-brand-border p-5 shadow-sm hover:shadow-md transition-shadow duration-200">
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="text-[11px] font-medium uppercase tracking-wider text-brand-text-muted">
          {label}
        </p>
        <p className="mt-2 text-3xl font-semibold tracking-tight text-brand-text leading-none tabular-nums">
          {value}
        </p>
        {subtitle && (
          <p className="mt-1 text-[11px] text-brand-text-muted">{subtitle}</p>
        )}
      </div>
      <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${TONE_CHIP[tone]}`}>
        <Icon className="h-5 w-5" strokeWidth={1.75} aria-hidden />
      </div>
    </div>
  </div>
);

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const { addToast } = useToast();
  const [stats, setStats] = useState<DashboardStats>({
    todaysAppointments: 0,
    todaysGoogleOnly: 0,
    completedAppointments: 0,
    completedGoogleOnly: 0,
    pendingConfirmations: 0,
    noShows: 0,
  });
  const [pendingWebsite, setPendingWebsite] = useState<Appointment[]>([]);
  const [googleOnlyCount, setGoogleOnlyCount] = useState(0);
  const [upcomingMerged, setUpcomingMerged] = useState<UpcomingItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Bulk-import modal state
  const [showImport, setShowImport] = useState(false);
  const [importProviders, setImportProviders] = useState<Provider[]>([]);
  const [importProcedures, setImportProcedures] = useState<Procedure[]>([]);
  const [importOperatories, setImportOperatories] = useState<Operatory[]>([]);
  const [importProvider, setImportProvider] = useState<number | ''>('');
  const [importProcedure, setImportProcedure] = useState<number | ''>('');
  const [importOperatory, setImportOperatory] = useState<number | ''>('');
  const [importBusy, setImportBusy] = useState(false);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      const today = new Date().toISOString().split('T')[0];
      const now = new Date();
      const from = moment().subtract(60, 'days').toISOString();
      const to = moment().add(30, 'days').toISOString();

      const [upcomingResp, allResp, gevResp] = await Promise.allSettled([
        apiService.getUpcomingAppointments(),
        apiService.getAppointments(),
        apiService.listGoogleEvents(from, to),
      ]);

      const upcoming = upcomingResp.status === 'fulfilled' ? (upcomingResp.value.data.data || []) : [];
      const all = (allResp.status === 'fulfilled' ? (allResp.value.data.data || []) : []) as Appointment[];
      const gevs = (gevResp.status === 'fulfilled' ? (gevResp.value.data.data || []) : []) as GoogleCalendarEvent[];

      setPendingWebsite(
        all
          .filter(a => a.status === 'pending' && a.source === 'website')
          .sort((a, b) =>
            `${a.appointment_date} ${a.start_time}`.localeCompare(
              `${b.appointment_date} ${b.start_time}`
            )
          )
      );

      // Dedupe: Google events that already correspond to an app-appointment are app-tracked.
      const apptEventIds = new Set(all.map(a => a.google_calendar_event_id).filter(Boolean) as string[]);
      const apptIds = new Set(all.map(a => String(a.id)));
      const googleOnly = gevs
        .filter(g => !apptEventIds.has(g.id))
        .filter(g => !g.appointmentId || !apptIds.has(g.appointmentId));
      setGoogleOnlyCount(googleOnly.length);

      // Build merged upcoming list (next 7 days). Sort by start datetime ascending.
      const horizon = new Date(Date.now() + 7 * 86400000);
      const upcomingApptItems: UpcomingItem[] = upcoming.map((a: Appointment) => ({
        kind: 'appt' as const,
        sortKey: `${a.appointment_date}T${a.start_time}`,
        appt: a,
      }));
      const upcomingGoogleItems: UpcomingItem[] = googleOnly
        .filter(g => g.status !== 'cancelled')
        .filter(g => g.start?.dateTime || g.start?.date)
        .map(g => {
          const startSrc = g.start!.dateTime || g.start!.date!;
          const d = new Date(startSrc);
          return {
            kind: 'google' as const,
            sortKey: startSrc,
            gevent: g,
            date: moment(d).format('YYYY-MM-DD'),
            time: g.start?.dateTime ? moment(d).format('HH:mm') : '',
          };
        })
        .filter(it => {
          const d = new Date(it.sortKey);
          return d >= new Date() && d <= horizon;
        });
      const merged = [...upcomingApptItems, ...upcomingGoogleItems]
        .sort((a, b) => a.sortKey.localeCompare(b.sortKey));
      setUpcomingMerged(merged);

      const todaysAppointments = upcoming.filter((apt: Appointment) => apt.appointment_date === today).length;
      const completedAppointments = upcoming.filter((apt: Appointment) => apt.status === 'completed').length;
      const pendingConfirmations = all.filter((apt: Appointment) => apt.status === 'pending').length;
      const noShows = upcoming.filter((apt: Appointment) => apt.status === 'no_show').length;

      // Google-only contribution
      const todayStart = new Date(`${today}T00:00:00`);
      const todayEnd = new Date(`${today}T23:59:59`);
      const todaysGoogleOnly = googleOnly.filter(g => {
        if (g.status === 'cancelled') return false;
        const startSrc = g.start?.dateTime || g.start?.date;
        if (!startSrc) return false;
        const d = new Date(startSrc);
        return d >= todayStart && d <= todayEnd;
      }).length;
      const completedGoogleOnly = googleOnly.filter(g => {
        if (g.status === 'cancelled') return false;
        const endSrc = g.end?.dateTime || g.end?.date;
        if (!endSrc) return false;
        return new Date(endSrc) < now;
      }).length;

      setStats({
        todaysAppointments: todaysAppointments + todaysGoogleOnly,
        todaysGoogleOnly,
        completedAppointments: completedAppointments + completedGoogleOnly,
        completedGoogleOnly,
        pendingConfirmations,
        noShows,
      });
    } catch (err) {
      console.error('Error loading dashboard data:', err);
      setError('Dashboard-Daten konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  const openImportModal = async () => {
    setShowImport(true);
    if (importProviders.length === 0) {
      try {
        const [p, pr, o] = await Promise.all([
          apiService.getProviders(true),
          apiService.getProcedures(true),
          apiService.getOperatories(true),
        ]);
        setImportProviders(p.data.data || []);
        setImportProcedures(pr.data.data || []);
        setImportOperatories(o.data.data || []);
      } catch (e: any) {
        addToast(e?.response?.data?.message || 'Konnte Stammdaten nicht laden.', 'error');
      }
    }
  };

  const handleBulkImport = async () => {
    if (!importProvider || !importProcedure || !importOperatory) {
      addToast('Bitte Behandler, Behandlung und Raum auswählen.', 'error');
      return;
    }
    setImportBusy(true);
    try {
      const resp = await apiService.bulkConvertGoogleEvents({
        provider_id: Number(importProvider),
        procedure_id: Number(importProcedure),
        operatory_id: Number(importOperatory),
      });
      if (resp.data?.success) {
        const d = resp.data.data;
        addToast(`✅ ${d.converted} importiert, ${d.skipped} übersprungen${d.errors.length ? `, ${d.errors.length} Fehler` : ''}.`, d.errors.length ? 'info' : 'success');
        if (d.errors.length) {
          console.warn('Bulk import errors:', d.errors);
        }
        setShowImport(false);
        await loadDashboardData();
      } else {
        addToast(resp.data?.message || 'Import fehlgeschlagen.', 'error');
      }
    } catch (e: any) {
      addToast(e?.response?.data?.message || 'Import fehlgeschlagen.', 'error');
    } finally {
      setImportBusy(false);
    }
  };

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString('de-DE', {
      weekday: 'short',
      day: '2-digit',
      month: 'short',
    });

  const formatTime = (timeString: string) =>
    new Date(`2000-01-01T${timeString}`).toLocaleTimeString('de-DE', {
      hour: '2-digit',
      minute: '2-digit',
    });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-brand-border border-t-brand-primary"></div>
      </div>
    );
  }

  const todayLong = new Date().toLocaleDateString('de-DE', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  return (
    <div className="space-y-6">
      {/* Hero banner */}
      <div className="relative overflow-hidden rounded-2xl shadow-md ring-1 ring-black/5 group">
        <img
          src="/images/admin-header-palma.webp"
          alt=""
          aria-hidden
          className="absolute inset-0 h-full w-full object-cover object-center scale-105 group-hover:scale-[1.08] transition-transform duration-[1500ms] ease-out"
        />
        <div
          className="absolute inset-0 bg-gradient-to-r from-brand-primary/95 via-brand-primary/70 to-brand-primary/15"
          aria-hidden
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" aria-hidden />
        <div
          className="absolute -right-8 -top-8 h-40 w-40 rounded-full bg-brand-accent/20 blur-3xl"
          aria-hidden
        />

        <div className="relative px-6 py-7 sm:px-8 sm:py-9">
          <div className="flex items-center gap-2 mb-2">
            <span className="inline-block h-px w-8 bg-brand-accent" aria-hidden />
            <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-brand-accent">
              Palma de Mallorca
            </span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-semibold text-white leading-tight tracking-tight">
            {getGreeting()}{user?.firstName ? `, ${user.firstName}` : ''}
          </h1>
          <p className="text-sm text-white/85 mt-1.5 max-w-md">
            Dein Überblick — heute ist <span className="font-medium text-white">{todayLong}</span>.
          </p>
        </div>
      </div>

      {error && (
        <div className="rounded-lg bg-rose-50 dark:bg-rose-500/10 border border-rose-200 dark:border-rose-500/30 px-4 py-3">
          <p className="text-sm font-medium text-rose-800 dark:text-rose-300">{error}</p>
        </div>
      )}

      {/* Stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={CalendarIcon}
          label="Termine heute"
          value={stats.todaysAppointments}
          tone="primary"
          subtitle={stats.todaysGoogleOnly > 0 ? `${stats.todaysGoogleOnly} davon nur in Google` : undefined}
        />
        <StatCard
          icon={CalendarCheck}
          label="Abgeschlossen"
          value={stats.completedAppointments}
          tone="success"
          subtitle={stats.completedGoogleOnly > 0 ? `${stats.completedGoogleOnly} davon nur in Google` : undefined}
        />
        <StatCard
          icon={CalendarClock}
          label="Offene Bestätigungen"
          value={stats.pendingConfirmations}
          tone="warning"
        />
        <StatCard
          icon={CalendarX}
          label="Nicht erschienen"
          value={stats.noShows}
          tone="danger"
        />
      </div>

      {/* Google-only events banner with bulk-import action */}
      {googleOnlyCount > 0 && (
        <section className="rounded-xl bg-brand-surface border border-brand-border shadow-sm p-4 flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0 flex-1">
            <h3 className="text-sm font-semibold text-brand-text">Aus Google Calendar nicht erfasst</h3>
            <p className="text-xs text-brand-text-muted mt-0.5">
              <span className="font-semibold text-brand-text tabular-nums">{googleOnlyCount}</span> Event(s) liegen nur im Google Calendar und sind nicht als App-Termin erfasst.
              Du kannst sie einzeln im Kalender editieren oder per Bulk-Import als App-Termine übernehmen.
            </p>
          </div>
          <button
            onClick={openImportModal}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-md text-white bg-brand-primary hover:bg-brand-primary-hover shadow-sm transition-colors"
          >
            Alle importieren…
          </button>
        </section>
      )}

      {/* Bulk-Import Modal */}
      {showImport && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => !importBusy && setShowImport(false)}>
          <div className="bg-brand-surface rounded-xl shadow-xl w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-brand-border">
              <h2 className="text-lg font-bold text-brand-text">Google-Events als App-Termine importieren</h2>
              <p className="text-xs text-brand-text-muted mt-1">
                Default-Werte für alle <span className="font-semibold">{googleOnlyCount}</span> Events. Du kannst die App-Termine danach einzeln anpassen.
              </p>
            </div>
            <div className="p-6 space-y-3">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-brand-text-muted mb-1">Standard-Behandler</label>
                <select value={importProvider} onChange={(e) => setImportProvider(e.target.value ? Number(e.target.value) : '')} className="w-full p-2 border border-brand-border rounded text-sm bg-brand-surface text-brand-text">
                  <option value="">Behandler wählen…</option>
                  {importProviders.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-brand-text-muted mb-1">Standard-Behandlung</label>
                <select value={importProcedure} onChange={(e) => setImportProcedure(e.target.value ? Number(e.target.value) : '')} className="w-full p-2 border border-brand-border rounded text-sm bg-brand-surface text-brand-text">
                  <option value="">Behandlung wählen…</option>
                  {importProcedures.map(p => {
                    const name = typeof p.name === 'string' ? p.name : (p.name as any).de || (p.name as any).en || '?';
                    return <option key={p.id} value={p.id}>{name}</option>;
                  })}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-brand-text-muted mb-1">Standard-Raum</label>
                <select value={importOperatory} onChange={(e) => setImportOperatory(e.target.value ? Number(e.target.value) : '')} className="w-full p-2 border border-brand-border rounded text-sm bg-brand-surface text-brand-text">
                  <option value="">Raum wählen…</option>
                  {importOperatories.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                </select>
              </div>
              <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                ⚠️ Achtung: Termine werden als <code>scheduled</code> angelegt. Bei zeitlichen Überschneidungen mit anderen scheduled/confirmed-Terminen wird ein Import übersprungen — diese siehst du in der Browser-Konsole.
              </p>
            </div>
            <div className="px-6 py-4 border-t border-brand-border flex justify-end gap-2">
              <button onClick={() => setShowImport(false)} disabled={importBusy} className="px-3 py-1.5 text-sm border border-brand-border rounded text-brand-text hover:bg-brand-surface-2 disabled:opacity-50">Abbrechen</button>
              <button onClick={handleBulkImport} disabled={importBusy} className="px-4 py-1.5 text-sm bg-brand-primary text-white rounded font-semibold disabled:opacity-50">
                {importBusy ? 'Importiere…' : 'Jetzt importieren'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pending website bookings */}
      {pendingWebsite.length > 0 && (
        <section className="rounded-xl bg-brand-surface border border-amber-200 dark:border-amber-500/30 shadow-sm overflow-hidden">
          <header className="flex items-center justify-between gap-3 px-6 py-4 bg-amber-50/60 dark:bg-amber-500/5 border-b border-amber-200/60 dark:border-amber-500/20">
            <div className="flex items-center gap-3 min-w-0">
              <div className="relative flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-400">
                <Bell className="h-[18px] w-[18px]" strokeWidth={1.75} aria-hidden />
                <span
                  aria-hidden
                  className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-amber-500 ring-2 ring-brand-surface animate-pulse"
                />
              </div>
              <div className="min-w-0">
                <h2 className="text-sm font-semibold text-brand-text">
                  Neue Buchungen über seadent.es
                </h2>
                <p className="text-xs text-brand-text-muted">
                  {pendingWebsite.length} ausstehend — bitte bestätigen oder ablehnen
                </p>
              </div>
            </div>
            <StatusPill tone="warning" dot>
              {pendingWebsite.length}
            </StatusPill>
          </header>

          <ul className="divide-y divide-brand-border">
            {pendingWebsite.map(a => (
              <li key={a.id}>
                <Link
                  to={`/admin/calendar?date=${a.appointment_date}&id=${a.id}`}
                  className="block px-6 py-4 hover:bg-brand-surface-2 transition-colors cursor-pointer focus:outline-none focus-visible:bg-brand-surface-2"
                  title="Im Kalender öffnen"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold text-brand-text">
                          {a.patient_name}
                        </span>
                        <StatusPill tone="warning">Website</StatusPill>
                      </div>
                      <div className="mt-1.5 flex items-center gap-1.5 text-xs text-brand-text-muted">
                        <CalendarIcon className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden />
                        <span>
                          {formatDate(a.appointment_date)} · {formatTime(a.start_time)}–{formatTime(a.end_time)}
                        </span>
                      </div>
                      <div className="mt-1 flex items-center gap-3 text-xs text-brand-text-muted flex-wrap">
                        <span className="inline-flex items-center gap-1">
                          <Smile className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden />
                          {a.procedure_name}
                        </span>
                        <span className="inline-flex items-center gap-1">
                          <Stethoscope className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden />
                          {a.provider_name}
                        </span>
                        <span className="inline-flex items-center gap-1">
                          <Building2 className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden />
                          {a.operatory_name}
                        </span>
                      </div>
                      <div className="mt-1 flex items-center gap-3 text-xs text-brand-text-muted flex-wrap">
                        <span className="inline-flex items-center gap-1">
                          <Mail className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden />
                          {a.patient_email}
                        </span>
                        <span className="inline-flex items-center gap-1">
                          <Phone className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden />
                          {a.patient_phone}
                        </span>
                      </div>
                      {a.notes && (
                        <div className="mt-2 text-xs text-brand-text whitespace-pre-line bg-brand-surface-2 px-3 py-2 rounded-md border border-brand-border">
                          {a.notes}
                        </div>
                      )}
                    </div>
                    <ChevronRight
                      className="h-4 w-4 text-brand-text-muted shrink-0 mt-1"
                      strokeWidth={2}
                      aria-hidden
                    />
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Upcoming appointments (merged: app + google-only) */}
      <section className="rounded-xl bg-brand-surface border border-brand-border shadow-sm overflow-hidden">
        <header className="px-6 py-4 border-b border-brand-border">
          <h2 className="text-sm font-semibold text-brand-text">Anstehende Termine</h2>
          <p className="text-xs text-brand-text-muted mt-0.5">Nächste 7 Tage — App-Termine und Google-Calendar-Events</p>
        </header>
        <ul className="divide-y divide-brand-border">
          {upcomingMerged.length === 0 ? (
            <li className="px-6 py-10 text-center text-sm text-brand-text-muted">
              Keine anstehenden Termine
            </li>
          ) : (
            upcomingMerged.slice(0, 10).map(item => {
              if (item.kind === 'appt') {
                const appointment = item.appt;
                const meta = STATUS_META[appointment.status] ?? { label: appointment.status, tone: 'neutral' as StatusTone };
                return (
                  <li key={`appt-${appointment.id}`}>
                    <Link
                      to={`/admin/calendar?date=${appointment.appointment_date}&id=${appointment.id}`}
                      className="block px-6 py-3.5 hover:bg-brand-surface-2 transition-colors cursor-pointer focus:outline-none focus-visible:bg-brand-surface-2"
                      title="Im Kalender öffnen"
                    >
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-accent/15 text-brand-primary text-sm font-semibold">
                            {appointment.patient_name.charAt(0).toUpperCase()}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-brand-text truncate">
                              {appointment.patient_name}
                            </p>
                            <p className="text-xs text-brand-text-muted">
                              {formatDate(appointment.appointment_date)} · {formatTime(appointment.start_time)}
                            </p>
                          </div>
                        </div>
                        <div className="hidden sm:flex flex-col items-end leading-tight text-xs text-brand-text-muted min-w-0">
                          <span className="truncate max-w-[180px]">{appointment.provider_name}</span>
                          <span className="truncate max-w-[180px]">{appointment.procedure_name}</span>
                        </div>
                        <StatusPill tone={meta.tone} dot>{meta.label}</StatusPill>
                      </div>
                    </Link>
                  </li>
                );
              }
              // google-only event
              const g = item.gevent;
              const title = g.summary || '(ohne Titel)';
              return (
                <li key={`gcal-${g.id}`}>
                  <Link
                    to={`/admin/calendar?date=${item.date}`}
                    className="block px-6 py-3.5 hover:bg-brand-surface-2 transition-colors cursor-pointer focus:outline-none focus-visible:bg-brand-surface-2"
                    title="Im Kalender öffnen (Event nur in Google Calendar)"
                  >
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gray-200 text-gray-600 text-sm font-semibold border border-dotted border-gray-400" title="Nur in Google Calendar">
                          G
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-brand-text truncate">
                            {title}
                          </p>
                          <p className="text-xs text-brand-text-muted">
                            {formatDate(item.date)}{item.time ? ` · ${item.time}` : ' · ganztägig'}
                          </p>
                        </div>
                      </div>
                      <div className="hidden sm:flex flex-col items-end leading-tight text-xs text-brand-text-muted min-w-0">
                        {g.location && <span className="truncate max-w-[180px]">{g.location}</span>}
                      </div>
                      <StatusPill tone="neutral">Google</StatusPill>
                    </div>
                  </Link>
                </li>
              );
            })
          )}
        </ul>
      </section>

      {/* Quick actions */}
      <section>
        <h2 className="text-xs font-semibold uppercase tracking-wider text-brand-text-muted mb-3">
          Schnellzugriff
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <QuickAction to="/admin/calendar" icon={CalendarIcon} title="Kalender" desc="Termine verwalten" />
          <QuickAction to="/admin/appointments" icon={ClipboardList} title="Terminliste" desc="Alle Termine filtern" />
          <QuickAction to="/admin/providers" icon={Stethoscope} title="Behandler" desc="Team & Kalender" />
        </div>
      </section>
    </div>
  );
};

const QuickAction: React.FC<{
  to: string;
  icon: LucideIcon;
  title: string;
  desc: string;
}> = ({ to, icon: Icon, title, desc }) => (
  <Link
    to={to}
    className="group flex items-center gap-3 rounded-xl bg-brand-surface border border-brand-border p-4 hover:border-brand-accent/60 hover:shadow-sm transition-all duration-200 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/40"
  >
    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-brand-accent/10 text-brand-primary group-hover:bg-brand-accent/20 transition-colors">
      <Icon className="h-5 w-5" strokeWidth={1.75} aria-hidden />
    </div>
    <div className="min-w-0 flex-1">
      <p className="text-sm font-semibold text-brand-text">{title}</p>
      <p className="text-xs text-brand-text-muted">{desc}</p>
    </div>
    <ChevronRight
      className="h-4 w-4 text-brand-text-muted shrink-0 group-hover:text-brand-primary group-hover:translate-x-0.5 transition-all"
      strokeWidth={2}
      aria-hidden
    />
  </Link>
);

export default Dashboard;
